/**
 * ATL-1041 — "a write derived from a read must name only the leaves it changes".
 *
 * ## The defect this exists to close
 *
 * Several edit paths seed a form from the in-memory read model and then hand the WHOLE form back
 * to a Firestore writer (`products.ts` `updateProduct` is the canonical one; its composite-cost
 * cascade a few lines later is worse, because it writes an entire read-model product object with
 * no user involvement at all). While the read model is populated from Firestore that is merely
 * wasteful. After an organization's read cutover the read model is populated from Supabase, and
 * the same code becomes a laundering channel:
 *
 *   Firestore doc --(never read)-->  ...
 *   Supabase row --> read transform --> form --> Firestore doc   ← overwrites what was never read
 *
 * Any field the read transform drops, renames or renders lossily is then written OVER the
 * Firestore document. That is not a display bug: Firestore is the campaign's rollback copy, so
 * the gap stops being reversible and becomes permanent corruption of the only clean side.
 *
 * This is not hypothetical. ATL-1012 records the mechanism already firing once: ATL-911's
 * transform invented `salePrices.<storeId>_retail` keys, the read->write round trip persisted
 * them into Firestore (46 keys / 7 orgs), and they are still there after the transform stopped
 * inventing them. The write-back is what made a transient transform bug permanent.
 *
 * ## The rule
 *
 * A write composed from a read must not re-assert values it merely read back. Concretely, given
 * the submitted payload and the read-model value the form was seeded from:
 *
 *  - a top-level field whose value is unchanged is NOT written;
 *  - a changed MAP field is written as dotted Firestore field paths for the changed leaves only,
 *    so sibling keys that the read transform never showed us survive untouched;
 *  - a key the payload does not carry is NEVER deleted — see "why nothing is ever deleted" below;
 *  - arrays, scalars, Dates and anything non-plain are written whole when they change (an array
 *    has no stable leaf identity — see LIMITS below).
 *
 * The load-bearing property is: **we never overwrite or delete a key we did not observe.** That is
 * what makes the write safe against a Firestore document we cannot see, which is exactly the
 * situation after a read cutover.
 *
 * ## Why nothing is ever deleted
 *
 * An omitted map key is ambiguous at a writer seam: it can mean "the user removed this" or "this
 * caller never mentioned this store". `updateProduct` alone receives both — a complete
 * `ProductDetailScreen` form and a partial CSV-import or supply-line payload — and it cannot tell
 * them apart. Guessing wrong deletes a price or a stock figure.
 *
 * The tie-breaker is that the OTHER store of record has already answered the same question the
 * same way: `update_product_atomic` writes `product_store_prices` with
 * `INSERT ... ON CONFLICT DO UPDATE` and there is no `DELETE` anywhere on the update path, so a
 * key omitted from a Supabase `salePrices` payload is left alone. Treating omission as "not
 * mentioned" on the Firestore side is therefore not a limitation — it is what makes the two
 * stores agree. A screen that genuinely needs to remove a key must say so explicitly, and the
 * removal is then visible in its own payload rather than inferred from an absence.
 *
 * ## Why not the alternatives
 *
 *  - *Seed the form from Firestore instead.* Re-introduces the Firestore read the cutover exists
 *    to remove, must be repeated per screen, and does nothing at the writer seam — Phase 6
 *    (remove Firestore writes) would re-open it.
 *  - *Turn `products.ts`'s `EXCLUDED_FIELDS` into an allowlist.* It cannot work: `EXCLUDED_FIELDS`
 *    filters the **Supabase** `products.update` DTO, and is built AFTER `batch.commit()`. It has
 *    never guarded the Firestore write at all. (See the note in `updateProduct`.) An allowlist
 *    there would also answer the wrong question — the danger is not "which fields may travel", it
 *    is "which VALUES were actually chosen by a human".
 *  - *Scope to react-hook-form's `dirtyFields`.* Depends on every `setValue` remembering
 *    `shouldDirty: true`; one omission silently drops a real user edit. Diffing against the seed
 *    cannot lose an edit, and it is enforceable at the writer seam where every caller passes.
 *
 * ## LIMITS — read before trusting this
 *
 *  1. **Arrays are written whole.** If the read transform dropped an element (ATL-911:
 *     `productImages` rows with `image_url IS NULL`) and the user edits the array, the surviving
 *     elements are what lands. Only a field the user did not touch is protected. Fixing that
 *     requires the transform to stop losing elements; this helper cannot invent them.
 *  2. **An unknown baseline fails OPEN.** `baseline == null` means "we have no idea what the form
 *     was seeded from", and dropping the payload would silently discard a real edit. Everything is
 *     written, i.e. exactly today's behaviour. Same principle as `getKnownStoreIds()` returning
 *     `null` rather than an empty set.
 *  3. **Keys that are not legal Firestore field-path segments** (containing `.`, `` ` ``, `[`,
 *     `]`, `*`, `/`, or empty) make the whole map fall back to a whole-value write, because a
 *     dotted path would address the wrong field. Preserved keys are lost in that case — the
 *     fallback is today's behaviour, not an improvement, and `unsafeKeyFields` reports it so the
 *     caller can make it visible rather than silent.
 */

/** A value we are willing to descend into and address by dotted field path. */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false
  if (Array.isArray(value)) return false
  if (value instanceof Date) return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/**
 * Firestore field paths are dot-separated; these characters make a segment un-addressable without
 * backtick quoting, which this codebase never emits. A map holding one is written whole instead.
 */
const FIELD_PATH_UNSAFE = /[.`[\]*/]/

const isFieldPathSafeKey = (key: string): boolean => key.length > 0 && !FIELD_PATH_UNSAFE.test(key)

/**
 * Structural equality for values that have already been through `sanitizeForFirestore`
 * (DocumentReferences dropped, Timestamps converted to Date). Deliberately STRICT: `'300'` and
 * `300` are different, because a type change in Firestore is a real change and must be written.
 */
export const isSameStoredValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, i) => isSameStoredValue(item, b[i]))
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && isSameStoredValue(a[key], b[key]))
  }
  // NaN is not === itself, and Firestore does store it (ATL-880). Two NaNs are "unchanged".
  if (typeof a === 'number' && typeof b === 'number') return Number.isNaN(a) && Number.isNaN(b)
  return false
}

/** "Nothing was entered here" — used only to decide whether an ABSENT baseline permits a write. */
const isEmptyValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true
  if (Array.isArray(value)) return value.length === 0
  if (isPlainObject(value)) return Object.keys(value).length === 0
  return false
}

export interface WriteBackDeltaOptions {
  /**
   * Fields written on every update regardless of the diff — the change-provenance metadata. They
   * describe THIS write, not the entity's state, so "unchanged" is meaningless for them.
   */
  alwaysWrite?: ReadonlySet<string>
}

export interface WriteBackDelta {
  /**
   * The payload for a Firestore `update()`. Keys may be dotted field paths. Empty means the
   * submitted payload asserted nothing the read model did not already hold.
   */
  update: Record<string, unknown>
  /** Top-level field names that were dropped as unchanged. Diagnostics only. */
  droppedFields: string[]
  /**
   * Map fields written WHOLE because a key was not field-path-safe (LIMIT 3). Non-empty means
   * this write did not get the protection this module exists to provide.
   */
  unsafeKeyFields: string[]
}

/**
 * Build the Firestore `update()` payload that asserts only what the submitted form actually
 * changed relative to the read-model value it was seeded from.
 *
 * @param submitted  the payload the caller wants to write (already sanitized for Firestore)
 * @param baseline   the read-model value of the same entity, sanitized the same way; `null`/
 *                   `undefined` means "unknown" and makes this a pass-through (LIMIT 2)
 */
export const buildWriteBackDelta = (
  submitted: Record<string, unknown>,
  baseline: Record<string, unknown> | null | undefined,
  options: WriteBackDeltaOptions = {},
): WriteBackDelta => {
  const alwaysWrite = options.alwaysWrite ?? new Set<string>()
  const update: Record<string, unknown> = {}
  const droppedFields: string[] = []
  const unsafeKeyFields: string[] = []

  if (baseline == null) {
    return { update: { ...submitted }, droppedFields, unsafeKeyFields }
  }

  for (const [field, value] of Object.entries(submitted)) {
    if (alwaysWrite.has(field)) {
      update[field] = value
      continue
    }

    const baselineHasField = Object.prototype.hasOwnProperty.call(baseline, field)
    const baselineValue = baselineHasField ? baseline[field] : undefined

    if (baselineHasField && isSameStoredValue(value, baselineValue)) {
      droppedFields.push(field)
      continue
    }

    // A map the form could edit: address the changed leaves, leave every unobserved sibling alone.
    // A baseline that is absent (or was not a map) is treated as `{}`: the form showed the user
    // nothing there, so nothing there can have been deliberately removed.
    if (isPlainObject(value)) {
      const baselineMap = isPlainObject(baselineValue) ? baselineValue : {}
      if (!baselineHasField && Object.keys(value).length === 0) {
        // The read transform never showed this field and the form submits nothing for it. Writing
        // `{}` here is precisely the corruption this module exists to prevent.
        droppedFields.push(field)
        continue
      }
      const leaves = collectLeafPaths(field, value, baselineMap)
      if (leaves === null) {
        unsafeKeyFields.push(field)
        update[field] = value
        continue
      }
      Object.assign(update, leaves)
      if (Object.keys(leaves).length === 0) droppedFields.push(field)
      continue
    }

    // Not a map. Only an ABSENT baseline needs care: an empty submitted value cannot be a
    // deliberate clear if the form never displayed anything to clear.
    if (!baselineHasField && isEmptyValue(value)) {
      droppedFields.push(field)
      continue
    }

    update[field] = value
  }

  return { update, droppedFields, unsafeKeyFields }
}

/**
 * Walk a map, emitting `prefix.key` entries for the changed and new leaves only.
 *
 * Returns `null` — meaning "write the whole map instead" — if any key on either side is not a
 * legal field-path segment, since a dotted path built from it would address a different field.
 * The baseline's keys participate in that safety check even though they are never written: a
 * baseline key that is not path-safe means the map as a whole cannot be addressed leaf-wise.
 */
const collectLeafPaths = (
  prefix: string,
  submitted: Record<string, unknown>,
  baseline: Record<string, unknown>,
): Record<string, unknown> | null => {
  const out: Record<string, unknown> = {}
  const keys = new Set([...Object.keys(submitted), ...Object.keys(baseline)])
  for (const key of keys) {
    if (!isFieldPathSafeKey(key)) return null
  }

  for (const key of keys) {
    const path = `${prefix}.${key}`
    const inSubmitted = Object.prototype.hasOwnProperty.call(submitted, key)
    const inBaseline = Object.prototype.hasOwnProperty.call(baseline, key)

    // Present in the baseline, absent from the payload: "not mentioned", never "removed".
    if (!inSubmitted) continue

    const value = submitted[key]
    if (inBaseline && isSameStoredValue(value, baseline[key])) continue

    if (isPlainObject(value)) {
      const nestedBaseline = isPlainObject(baseline[key]) ? (baseline[key] as Record<string, unknown>) : {}
      const nested = collectLeafPaths(path, value, nestedBaseline)
      if (nested === null) return null
      Object.assign(out, nested)
      continue
    }

    if (!inBaseline && isEmptyValue(value)) continue

    out[path] = value
  }

  return out
}
